import PromotionFillIcon from '@/components/Icons/PromotionFill';
import XIcon from '@/components/Icons/X';
import { useHeaderScroll } from '@/components/NewHeader/useHeaderScroll';
import { useTranslation } from '@/contexts/dictionaryContext';
import { cn } from '@/lib/utils';
import Link from 'next/link';

export function DataList({
  data,
  smallContainer = false,
  scrollable = false,
  onItemClick,
}: {
  data: {
    title: string;
    items: {
      title: string;
      href: string;
    }[];
  };
  smallContainer?: boolean;
  scrollable?: boolean;
  onItemClick: (item: { title: string; href: string }) => void;
}) {
  return (
    <div className={cn(smallContainer ? 'space-y-6' : 'space-y-4')}>
      <p className={cn('font-bold', smallContainer && 'uppercase')}>
        {data.title}
      </p>
      <div
        className={cn('space-y-2', scrollable && 'max-h-40 overflow-y-auto')}>
        {data.items.map((item) => (
          <CatalogLink
            onClick={() => onItemClick(item)}
            key={item.title}
            href={item.href}>
            {item.title}
          </CatalogLink>
        ))}
      </div>
    </div>
  );
}

export function CatalogLink({
  children,
  href,
  onClick,
  className,
}: React.PropsWithChildren<
  React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    href: string;
  }
>) {
  const { close } = useHeaderScroll();

  function handleCloseClick(
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) {
    if (onClick) {
      onClick(e);
    }
    close();
  }

  return (
    <div>
      <Link
        href={href}
        className={cn(
          'text-gray-800 hover:text-gray-900 nav-styled-link transition-colors',
          className,
        )}
        onClick={handleCloseClick}>
        {children}
      </Link>
    </div>
  );
}

export function CloseButton({ className }: { className?: string }) {
  const { close } = useHeaderScroll();
  const { newHeader } = useTranslation();

  return (
    <button
      className={cn(
        'group flex justify-center items-center cursor-pointer leading-4 tracking-[2px]',
        'text-gray-850 hover:text-gray-900 text-center',
        'fill-gray-850 hover:fill-gray-900 transition-colors',
        'uppercase text-sm font-bold px-4 py-3',
        className,
      )}
      onClick={close}>
      {newHeader.navMenu.close} <XIcon className="size-5 fill-inherit" />
    </button>
  );
}

export function PromotionalOffersList({
  title,
  items,
  onItemClick,
}: {
  title: string;
  items: { title: string; href: string }[];
  onItemClick: (item: { title: string; href: string }) => void;
}) {
  if (!items.length) {
    return null;
  }

  return (
    <div className="space-y-4 border border-primary/25 bg-primary/5 px-4 py-5">
      <p className="flex items-center gap-2 font-bold uppercase text-primary">
        <PromotionFillIcon className="size-4 shrink-0 text-primary" />
        {title}
      </p>
      <div className="space-y-2 max-h-24 overflow-y-auto">
        {items.map((item) => (
          <CatalogLink
            onClick={() => onItemClick(item)}
            key={item.href}
            href={item.href}
            className="text-primary hover:text-primary/80 font-medium">
            {item.title}
          </CatalogLink>
        ))}
      </div>
    </div>
  );
}
